import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'

import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'
import { blobTypes, downloadFile, stateFullLabel, stateLabel } from '../../../../shared/helpers'
import { useIsReadOnly } from '../../../../shared/alumni'
import useDebouncedValue from '../../../../shared/useDebouncedValue'
import useLatestOnly from '../../../../shared/useLatestOnly'

import Btn from '../../../../UI/Btn'
import Icon from '../../../../UI/Icon'
import Table from '../../../../UI/Table'
import Search from '../../../../UI/Search'
import FilterPanel from '../../../../UI/FilterPanel'
import useFilters from '../../../../UI/FilterPanel/useFilters'
import {
	branchField,
	createdWithinField,
	parentalConfirmationField,
	roleField,
	schoolField,
	schoolTypeField,
	stateField,
	verifiedTeacherField
} from '../../../../UI/FilterPanel/fields'
import BtnFilter from '../../../../UI/BtnFilter'
import MiniHeader from '../../../../UI/MiniHeader'
import BtnTooltip from '../../../../UI/BtnTooltip'
import Pagination from '../../../../UI/Pagination'

import eventEmitter, { types } from '../../../../shared/eventEmitter'

import styles from './style.module.scss'
import { canRemoveStudent, removeStudentAccount } from './removeStudent'
import { canRemoveTeacher, removeTeacherAccount } from './removeTeacher'
import { logIn, setTeacherCompanyView } from '../../../../redux/ducks/user'
import { connect } from 'react-redux'
import { formatVienna } from '../../../../shared/datetime'

function Users(props) {
	const { t, i18n } = useTranslation()
	const [users, setUsers] = useState([])
	const [countAll, setCountAll] = useState(0)
	const [page, setPage] = useState(0)
	const [sort, setSort] = useState({ name: 'firstName', direction: 'asc' })
	const [search, setSearch] = useState('')
	const [openFilter, setOpenFilter] = useState(false)
	const [loading, setLoading] = useState(false)
	const readOnly = useIsReadOnly()
	const limit = 10

	// Bundesland-Admins sehen ohnehin nur ihr eigenes Bundesland (das setzt das
	// Backend), ein Filter darauf waere eine Auswahl ohne Wirkung.
	const fields = useMemo(
		() => [
			...(props.role === TRoles.ADMIN ? [] : [stateField(t)]),
			roleField(t),
			verifiedTeacherField(t),
			/*
			 * Elternbestätigung: Ziel der Kachel „Schüler:innen · Ausstehend"
			 * auf der Startseite. „Eingereicht" heißt dort — wie im Widget —
			 * hochgeladen und noch nicht durchgesehen; genau die Zeilen, an
			 * denen das Menü „Elternbestätigung bestätigen" anbietet.
			 */
			parentalConfirmationField(t),
			branchField(t, props.branch, i18n.language),
			schoolTypeField(t, props.schoolTypes),
			schoolField(t),
			// Ziel der Kachel „Neu" auf der Startseite (shared/widgetListLink).
			createdWithinField(t)
		],
		[t, i18n.language, props.role, props.branch, props.schoolTypes]
	)

	const filters = useFilters({ storageKey: 'users', fields })
	const filterQuery = JSON.stringify(filters.query)
	const debouncedSearch = useDebouncedValue(search)
	const latest = useLatestOnly()

	const fetchUsers = () => {
		const isCurrent = latest()

		http.get(TApi.ADMIN_GET_USERS, {
			params: {
				limit,
				offset: page * limit,
				order: sort.name,
				direction: sort.direction,
				search: debouncedSearch,
				filter: filterQuery
			}
		}).then(({ data }) => {
			if (!isCurrent()) return

			const tableArray = data.rows.map(v => {
				return {
					...v,
					createdAt: formatVienna(v.createdAt, 'dd.MM.yyyy'),
					state:
						v.EmploymentCompany?.TeachersSupervisions?.[0]?.Teacher?.School?.state ||
						v.EmploymentCompany?.state ||
						v.School?.state ||
						v?.state ||
						'-'
				}
			})
			setUsers(tableArray)
			setCountAll(data.count)
		})
	}

	// Gefiltert und gesucht wird ohne Knopfdruck; jede Aenderung setzt die Seite
	// zurueck, sonst stuende man auf Seite 4 einer kuerzer gewordenen Liste.
	useEffect(() => {
		fetchUsers()

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, sort, debouncedSearch, filterQuery])

	const handleFilterChange = (key, selected) => {
		setPage(0)
		filters.setField(key, selected)
	}

	const handleFilterReset = () => {
		setPage(0)
		filters.resetAll()
	}

	const handleSearchChange = value => {
		setPage(0)
		setSearch(value)
	}

	const handleSort = (name, direction) => {
		setSort({ name, direction })
	}

	// Geöffnet wird das Profil — für Schüler:innen und Lehrkräfte gibt es eine
	// Maske, für die übrigen Rollen (etwa Verwaltung) nicht. Deren Zeilen
	// bleiben deshalb Text statt eines Klicks, der ins Leere ginge.
	const canOpenProfile = user => [TRoles.STUDENT, TRoles.TEACHER].includes(user.role)

	const openProfile = user => {
		if (!canOpenProfile(user)) return
		eventEmitter.emit(types.openStudentProfile, user, fetchUsers, getActions(user, { inProfile: true }))
	}

	/*
	 * Ansichtsmodus: Die Verwaltung sieht die Plattform mit den Augen einer
	 * Schüler:in oder — seit 02.11.2026 — einer Lehrkraft. Der Server stellt
	 * dafür einen Token aus, der nur lesen darf (`isTeacherStudentView`,
	 * `teacherStudentModeGuard` lässt nur GET durch); der eigene Token bleibt
	 * im Browser liegen, „Ansicht verlassen" kehrt zu ihm zurück
	 * (`components/AlertTeacherMode`).
	 */
	const handleUserMode = async userId => {
		const {
			data: { token }
		} = await http.get(TApi.ADMIN_GET_TOKEN_USER(userId))

		http.defaults.headers['Authorization'] = `Bearer ${token}`
		props.setTeacherCompanyView(true)
		const { data } = await http.get(TApi.COMMON_ME, { disableToast: true })
		props.logIn({
			role: data.role,
			profile: data
		})
	}

	/**
	 * Die Handlungen zu einer Person.
	 *
	 * Dieselbe Liste versorgt das Drei-Punkte-Menü der Zeile und das geöffnete
	 * Profil — dort ohne den Eintrag, der das Profil erst öffnet
	 * (`inProfile`), der wäre eine Schleife auf sich selbst.
	 */
	const getActions = (user, { inProfile } = {}) => {
		// Alumni (#213): keine Handlungen — weder „Als Benutzer:in ansehen"
		// noch Bearbeiten, Prüfen, Zusammenführen oder Herunterladen. Das Profil
		// öffnet die Zeile.
		if (readOnly) return null
		switch (user.role) {
			case TRoles.STUDENT: {
				const parentalConfirmation = (user.Documents || []).find(item => item.type === 'PARENTAL_CONFIRMATION')

				return [
					{
						label: t('view mode as Student'),
						icon: 'eye',
						hint: t('actionHintViewMode'),
						onClick: () => handleUserMode(user.id)
					},
					...(inProfile
						? []
						: [
								{
									label: t('Edit Userprofile'),
									icon: 'userEdit',
									onClick: () => openProfile(user)
								}
						  ]),
					{
						label: parentalConfirmation
							? t('Download parental consent')
							: t('Download parental consent (not uploaded yet!)'),
						icon: 'download',
						// Im Profil steht der Hinweis darunter — die Klammer im Titel
						// braucht es dort nicht.
						profileLabel: t('Download parental consent'),
						hint: parentalConfirmation ? t('actionHintDownloadParental') : t('actionHintParentalMissing'),
						disabled: !parentalConfirmation,
						onClick: () => {
							http.get(TApi.ADMIN_GET_FILES_DOCUMENT(parentalConfirmation.name), {
								headers: {
									'Content-Type': 'application/pdf,image/jpeg,image/png,image/gif'
								},
								responseType: 'blob'
							}).then(({ data }) => {
								downloadFile(`${user.firstName}-${user.lastName}: Parental Confirmation`, data)
							})
						}
					},
					{
						label: user.reviewedParentalConfirmation
							? t('Parental consent verfied')
							: t('Parental consent (need verifying)'),
						icon: 'documentCheck',
						profileLabel: user.reviewedParentalConfirmation
							? t('Parental consent verfied')
							: t('Mark parental consent as reviewed'),
						hint: user.reviewedParentalConfirmation
							? t('actionHintParentalReviewed')
							: parentalConfirmation
							? t('actionHintReviewParental')
							: t('actionHintParentalMissing'),
						disabled: user.reviewedParentalConfirmation || !parentalConfirmation,
						className: styles.actionGreen,
						onClick: () =>
							http.put(TApi.ADMIN_REVIEW_PARENTAL_CONFIRMATION(user.id)).then(() => {
								setUsers(current =>
									current.map(item =>
										item.id === user.id ? { ...item, reviewedParentalConfirmation: true } : item
									)
								)
								toast.success(t('Status was updated'))
							})
					},
					/*
					 * Konto löschen (Anforderung #226) — zuletzt und rot, weil
					 * endgültig. Aus dem Profil heraus schließt das Fenster nach
					 * dem Löschen: Die Person gibt es dann nicht mehr.
					 */
					...(canRemoveStudent(props.role, user)
						? [
								{
									label: t('Delete account'),
									icon: 'trash',
									hint: t('actionHintDeleteAccount'),
									danger: true,
									className: styles.actionDanger,
									onClick: closeModal =>
										removeStudentAccount({
											t,
											user,
											onDone: () => {
												if (typeof closeModal === 'function') closeModal()
												fetchUsers()
											}
										})
								}
						  ]
						: [])
				]
			}
			case TRoles.TEACHER: {
				return [
					// Wie bei Schüler:innen zuerst: die Plattform so sehen, wie
					// die Lehrkraft sie sieht — Dashboard, betreute Junior
					// Companies, Schüler:innen, Berichte.
					{
						label: t('view mode as Teacher'),
						icon: 'eye',
						hint: t('actionHintViewMode'),
						onClick: () => handleUserMode(user.id)
					},
					...(inProfile
						? []
						: [
								{
									label: t('profile'),
									icon: 'user',
									onClick: () => openProfile(user)
								}
						  ]),
					{
						label: t('Merge teacher'),
						icon: 'merge',
						hint: t('actionHintMergeTeacher'),
						// Die zusammengeführte Lehrkraft gibt es danach nicht mehr —
						// ein offenes Profil dazu wäre ein leerer Datensatz.
						onClick: closeModal => {
							if (typeof closeModal === 'function') closeModal()
							eventEmitter.emit(types.openMergeTeacherModal, user, fetchUsers)
						}
					},
					{
						label: t('Verify teacher'),
						icon: 'badgeCheck',
						hint: user.verifiedTeacher ? t('actionHintTeacherVerified') : t('actionHintVerifyTeacher'),
						disabled: user.verifiedTeacher,
						onClick: () =>
							http.put(TApi.ADMIN_ACTIVATE_TEACHER(user.id)).then(() => {
								toast.success(t('Teacher activated'))
								fetchUsers()
							})
					},
					// Konto löschen (Anforderung #239) — wie bei Schüler:innen zuletzt
					// und rot.
					...(canRemoveTeacher(props.role, user)
						? [
								{
									label: t('Delete account'),
									icon: 'trash',
									hint: t('actionHintDeleteAccount'),
									danger: true,
									className: styles.actionDanger,
									onClick: closeModal =>
										removeTeacherAccount({
											t,
											user,
											onDone: () => {
												if (typeof closeModal === 'function') closeModal()
												fetchUsers()
											}
										})
								}
						  ]
						: [])
				]
			}
			default: {
				return null
			}
		}
	}

	const columns = useMemo(
		() => [
			{
				Header: 'Name',
				accessor: 'firstName',
				minWidth: 250,
				Cell: ({ row }) => {
					// Für Verwaltungskonten gibt es keine Handlungen — der Name
					// gehört trotzdem in die Spalte, sonst steht die Zeile ohne
					// jede Beschriftung da.
					const actions = getActions(row.original)
					return (
						<div style={{ display: 'flex', justifyContent: 'space-between' }}>
							{row.original.firstName && row.original.lastName ? (
								<span style={{ width: '220px' }}>
									{row.original.firstName} {row.original.lastName}
								</span>
							) : (
								<span
									style={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										opacity: 0.5,
										width: '220px'
									}}
								>
									{t('Confirmation pending')}
								</span>
							)}
							{actions && <BtnTooltip items={actions} />}
						</div>
					)
				},
				maxWidth: 150
			},

			{
				Header: t('Role'),
				accessor: 'role',
				// Bis 07.08.2026 standen hier nur Anfangsbuchstaben ("S", "L") ohne
				// Legende — die Rolle steht jetzt ausgeschrieben da (#79).
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>{t(row.values.role)}</span>
				),
				width: 120
			},
			{
				Header: t('Company'),
				accessor: 'companyName',
				minWidth: 120,
				Cell: ({ row }) => {
					return (
						<span style={{ overflow: 'hidden' }}>
							{row.original.EmploymentCompany?.name || '-'}
						</span>
					)
				}
			},
			{
				Header: t('School'),
				accessor: 'school',
				minWidth: 120,
				Cell: ({ row }) => (
					<span style={{ overflow: 'hidden' }}>
						{row.original.School?.name ||
							row.original.EmploymentCompany?.TeachersSupervisions?.[0]?.Teacher?.School?.name ||
							'-'}
					</span>
				)
			},
			{
				Header: t('Fed. State'),
				accessor: 'state',
				width: 70,
				cellToolTip: ({ row }) => stateFullLabel(row.original.state),
				Cell: ({ row }) => stateLabel(row.original.state)
			},
			{
				Header: t('Registration date'),
				accessor: 'createdAt',
				width: 100
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, users, readOnly]
	)

	const downoloadList = fileType => {
		setLoading(true)
		http.get(TApi.ADMIN_DOWNOLOAD_USERS_LIST, {
			params: {
				type: fileType,
				leng: localStorage.getItem('i18nextLng')
			},
			responseType: 'arraybuffer'
		})
			.then(res => {
				let blob = new Blob([res.data], {
					type: blobTypes[fileType]
				})
				downloadFile(i18n.language === 'ger' ? 'Benutzerlnnen' : 'Users', blob)
			})
			.finally(() => {
				setLoading(false)
			})
	}

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.miniHeader} title={t('users')}>
				<div className={styles.miniHeaderContent}>
					<Search
						value={search}
						onChange={e => handleSearchChange(e.target.value)}
						placeholder={t('Find a user')}
						onSubmit={e => e.preventDefault()}
					/>
					<BtnFilter
						icon="arrow"
						onClick={() => setOpenFilter(true)}
						count={filters.count}
					>
						{t('Filter')} ({filters.count})
					</BtnFilter>
				</div>
			</MiniHeader>
			<div className={styles.subHeader}>
				<Pagination limit={limit} page={page} label={t('users')} countAll={countAll} onChange={setPage} />
				{!readOnly && (
					<div className={styles.downloadContent}>
						{t('Download list as')}
						<div>
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('XLS')
									}}
								>
									XLS
								</Btn>
							)}
							{loading && (
								<Icon
									className={clsx(styles.icon, styles.rootLoading)}
									size={{ w: 16, h: 16 }}
									name="loading"
									color={'#22404D'}
								/>
							)}
							{!loading && (
								<Btn
									theme="download"
									iconSize={{ w: 24, h: 24 }}
									icon="download"
									onClick={() => {
										downoloadList('PDF')
									}}
								>
									PDF
								</Btn>
							)}
						</div>
					</div>
				)}
			</div>
			<div className="container">
				<Table
					columns={columns}
					items={users}
					limit={limit}
					countAll={countAll}
					onChange={setPage}
					handleSort={handleSort}
					paginationLabel={t('users')}
					page={page}
					onRowClick={openProfile}
					isRowClickable={canOpenProfile}
					rowTitle={t('openRecord')}
				/>
			</div>
			<FilterPanel
				open={openFilter}
				onClose={() => setOpenFilter(false)}
				fields={fields}
				values={filters.values}
				count={filters.count}
				onChange={handleFilterChange}
				onResetAll={handleFilterReset}
			/>
		</div>
	)
}

const mapStateToProps = state => ({
	role: state.user.role,
	branch: state.options.branch,
	schoolTypes: state.options.schoolTypes
})

const mapDispatchToProps = dispatch => ({
	setTeacherCompanyView: data => dispatch(setTeacherCompanyView(data)),
	logIn: data => dispatch(logIn(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(Users)
